import type { ContractParty } from "@closer/shared";
import { Button, FileInput, FormField, Modal, Select, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";

type VersiyonSecenegi = { id: string; versionNumber: number };

/**
 * KARŞI TARAFTAN GELEN REVİZYONU YÜKLEME (CLOS-137 `[092]`).
 *
 * ⚠ Sunucu ucu aylardır hazırdı ama ekran yoktu: kullanıcı karşı tarafın gönderdiği
 * belgeyi ürünün hiçbir yerinden yükleyemiyordu.
 *
 * ⚠ GÖNDEREN TARAF ayrı bir alan. Kart: *"yükleyen gerçek kullanıcı ile belgenin kaynak
 * tarafı ayrı alanlar olarak korunur; kullanıcı karşı tarafın aktörü gibi
 * gösterilmez."* Belgeyi bizim tarafımızdan biri yüklüyor ama belge karşı tarafın.
 *
 * ⚠ Kendi çalışma alanımız gönderen olarak SUNULMUYOR: "karşı taraftan gelen revizyon"un
 * kaynağı tanımı gereği karşı taraf. Listelemek, kullanıcıyı sunucunun reddedeceği bir
 * seçime davet etmek olurdu.
 *
 * ⚠ Baz versiyon SEÇİLİYOR, varsayılmıyor. "En sonuncusu" diye sabitleseydik, karşı taraf
 * eski bir sürüm üzerinde çalışmışsa geçmiş yanlış kurulurdu — ve bu, sonradan fark
 * edilmesi en zor hatalardan biri.
 */
export function CounterpartyRevisionDialog({
  open,
  onClose,
  contractId,
  versiyonlar,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  contractId: string;
  versiyonlar: VersiyonSecenegi[];
  onConfirm: (girdi: { sourcePartyId: string; baseVersionId: string | null; dosya: File }) => void;
  isPending: boolean;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const [taraf, setTaraf] = useState("");
  const [baz, setBaz] = useState("");
  const [dosya, setDosya] = useState<File | null>(null);

  const taraflar = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "contract", contractId, "taraflar", "revizyon"),
    queryFn: async () =>
      (
        await api<{ parties: ContractParty[] }>(`/api/contracts/${contractId}/parties`, {
          tenantId,
        })
      ).data,
  });

  const gonderenler = (taraflar.data?.parties ?? []).filter((t) => t.partyType !== "workspace");

  useEffect(() => {
    if (!open) return;
    setTaraf("");
    setBaz("");
    setDosya(null);
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Karşı taraf revizyonu yükle"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            loading={isPending}
            disabled={taraf === "" || dosya === null}
            onClick={() => {
              if (!dosya) return;
              onConfirm({
                sourcePartyId: taraf,
                /** Boş seçim "baz yok" demek; boş metin göndermek şemaya takılırdı. */
                baseVersionId: baz === "" ? null : baz,
                dosya,
              });
            }}
          >
            Yükle
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {taraflar.isPending ? (
          <div role="status" aria-label="Taraflar yükleniyor">
            <Skeleton lines={2} />
          </div>
        ) : (
          <FormField label="Gönderen taraf" required>
            <Select
              aria-label="Gönderen taraf"
              value={taraf}
              onChange={(e) => setTaraf(e.target.value)}
            >
              <option value="">Seçin</option>
              {gonderenler.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.displayName}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        <FormField label="Baz versiyon">
          <Select aria-label="Baz versiyon" value={baz} onChange={(e) => setBaz(e.target.value)}>
            <option value="">Baz yok</option>
            {versiyonlar.map((v) => (
              <option key={v.id} value={v.id}>
                {`Versiyon ${v.versionNumber}`}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Revizyon dosyası">
          <FileInput
            aria-label="Revizyon dosyası seç"
            onChange={(e) => setDosya(e.target.files?.[0] ?? null)}
          />
        </FormField>
      </div>
    </Modal>
  );
}
